import React, { createContext, useContext, useEffect, useReducer, useCallback } from 'react';
import { User, AuthTokens } from '../types';
import type { AuthResponse } from '../api/types';
import { authAPI } from '../api';
import { clearSession } from '../api/session';

interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; tokens: AuthTokens } }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User };

interface AuthContextType extends AuthState {
  // Returns the signed-in user, so the caller can route by role without racing the
  // dispatch it just triggered.
  login: (email: string, password: string) => Promise<User>;
  /**
   * Finish "Sign in with BOS": trade the one-time code from the redirect for a session. The
   * result is the same session password sign-in produces, stored the same way.
   */
  completeBosSignIn: (code: string) => Promise<User>;
  register: (data: RegisterData) => Promise<void>;
  // Returns the refreshed user, because the caller needs the role in the NEW workspace
  // to decide where to navigate. Reading it from state instead races the dispatch, and
  // reading the pre-switch value routes an Owner in one workspace as whatever they were
  // in the other.
  switchPortal: (portal: string) => Promise<User | undefined>;
  /** Save your own name on the server and show it everywhere at once. */
  updateProfile: (fullName: string) => Promise<User>;
  logout: () => Promise<void>;
}

interface RegisterData {
  email: string;
  full_name: string;
  password: string;
  confirm_password: string;
  portal_name: string;
  invite_token?: string;
}

const initialState: AuthState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: true,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        tokens: action.payload.tokens,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'LOGOUT':
      return { ...initialState, isLoading: false };
    case 'UPDATE_USER':
      return { ...state, user: action.payload };
    default:
      return state;
  }
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Restore session from localStorage
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const storedTokens = localStorage.getItem('auth_tokens');
        const storedUser = localStorage.getItem('auth_user');
        if (storedTokens && storedUser) {
          const tokens = JSON.parse(storedTokens);
          const user = JSON.parse(storedUser);
          dispatch({ type: 'LOGIN_SUCCESS', payload: { user, tokens } });
          // Verify token is still valid
          try {
            const res = await authAPI.me();
            localStorage.setItem('auth_user', JSON.stringify(res.data));
            dispatch({ type: 'UPDATE_USER', payload: res.data });
          } catch (error: any) {
            // Only a server that says the session is over ends it. Any failure used to sign
            // people out — so opening the app during a deploy's few seconds of 502s, or on a
            // dropped connection, threw away a perfectly good session — and `clear()` wiped
            // every per-browser preference with it (column choices, recent projects).
            const status = error?.response?.status;
            if (status === 401 || status === 403) {
              dispatch({ type: 'LOGOUT' });
              clearSession();
            }
          }
        } else {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } catch {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };
    restoreSession();
  }, []);

  // One way to begin a session, whichever door the person came through.
  const startSession = useCallback((data: AuthResponse) => {
    const { user, access, refresh } = data;
    const tokens = { access, refresh };
    localStorage.setItem('auth_tokens', JSON.stringify(tokens));
    localStorage.setItem('auth_user', JSON.stringify(user));
    dispatch({ type: 'LOGIN_SUCCESS', payload: { user, tokens } });
    return user as User;
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await authAPI.login({ email, password });
    return startSession(res.data);
  }, [startSession]);

  const completeBosSignIn = useCallback(async (code: string) => {
    const res = await authAPI.bosExchange(code);
    return startSession(res.data);
  }, [startSession]);

  const register = useCallback(async (data: RegisterData) => {
    const res = await authAPI.register(data);
    const { user, access, refresh } = res.data;
    const tokens = { access, refresh };
    localStorage.setItem('auth_tokens', JSON.stringify(tokens));
    localStorage.setItem('auth_user', JSON.stringify(user));
    dispatch({ type: 'LOGIN_SUCCESS', payload: { user, tokens } });
  }, []);

  const switchPortal = useCallback(async (portal: string) => {
    const res = await authAPI.switchPortal(portal);
    const nextUser = res.data?.user || res.data;
    localStorage.setItem('auth_user', JSON.stringify(nextUser));
    dispatch({ type: 'UPDATE_USER', payload: nextUser });
    return nextUser as User | undefined;
  }, []);

  const updateProfile = useCallback(async (fullName: string) => {
    const res = await authAPI.updateMe({ full_name: fullName });
    const nextUser = res.data as unknown as User;
    localStorage.setItem('auth_user', JSON.stringify(nextUser));
    dispatch({ type: 'UPDATE_USER', payload: nextUser });
    return nextUser;
  }, []);

  const logout = useCallback(async () => {
    try {
      const tokens = state.tokens;
      if (tokens?.refresh) await authAPI.logout(tokens.refresh);
    } catch {}
    localStorage.removeItem('auth_tokens');
    localStorage.removeItem('auth_user');
    dispatch({ type: 'LOGOUT' });
  }, [state.tokens]);

  return (
    <AuthContext.Provider
      value={{ ...state, login, completeBosSignIn, register, switchPortal, updateProfile, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
